import {
    FC,
    ReactElement,
    useEffect,
    useRef,
    useState,
} from 'react';

import classNames from 'classnames';
import { useWindowSize } from 'react-use';

import { Picture } from '../../../../../components';
import { Button, VideoTheatre } from '../../../../../compositions';
import { MediaItem } from '../../../../../entities/Media/Media';
import { clamp } from '../../../../../helpers/number';
import { useTimeout, useTrans } from '../../../../../hooks';

import './ProductMediaSlider.scss';

interface ProductMediaSliderProps {
    mediaItems: MediaItem[];
    className?: string;
}

export const ProductMediaSlider: FC<ProductMediaSliderProps> = ({
    mediaItems,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const { width } = useWindowSize();

    const listRef = useRef<HTMLUListElement>(null);

    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [activeItemIndex, setActiveItemIndex] = useState<number>(0);
    const [cardWidth, setCardWidth] = useState<number>(0);
    const [scrollCounter, setScrollCounter] = useState<number>(0);

    useEffect((): void => {
        const firstChild = listRef.current?.firstChild as HTMLDivElement | undefined;

        if (firstChild) {
            setCardWidth(firstChild.offsetWidth);
        }
    }, [width]);

    useTimeout((): void => {
        if (listRef.current) {
            listRef.current.scrollLeft = 0;
        }

        setIsInitialized(!!cardWidth);
    }, 100, [cardWidth]);

    useTimeout((): void => {
        if (!listRef.current || !cardWidth || !isInitialized) {
            return;
        }

        const { scrollLeft } = listRef.current;
        const scrollPosition = (scrollLeft + 1) / cardWidth;
        const roundedPosition = Math.round(scrollPosition);
        const newIndex = clamp(roundedPosition, 0, mediaItems.length - 1);

        setActiveItemIndex(newIndex);
    }, 10, [scrollCounter]);

    const scrollToMediaItem = (index: number): void => {
        if (!listRef.current) {
            return;
        }

        setActiveItemIndex(index);

        const activeItem = listRef.current.children[index];

        if (activeItem) {
            activeItem.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        }
    };

    // TODO: Replace this and the timeout with scrollEnd once Safari supports it
    const handleScroll = (): void => {
        setScrollCounter((scrollCounter + 1) % 100);
    };

    return (
        <div className={`product-media-slider ${className}`}>
            {mediaItems.length > 1 && (
                <ul className="product-media-slider__bullet-list">
                    {mediaItems.map((mediaItem, index) => {
                        const handleSelect = (): void => {
                            scrollToMediaItem(index);
                        };

                        const bulletClassNames = classNames('product-media-slider__bullet', {
                            'product-media-slider__bullet--is-active': index === activeItemIndex,
                        });

                        return (
                            <li key={mediaItem.id} className="product-media-slider__bullet-item">
                                <Button
                                    text={trans('containers.productDetailRenderer.selectMediaItem')}
                                    onClick={handleSelect}
                                    className="product-media-slider__bullet-button"
                                >
                                    <div className={bulletClassNames} />
                                </Button>
                            </li>
                        );
                    })}
                </ul>
            )}

            <ul
                ref={listRef}
                onScroll={handleScroll}
                className="product-media-slider__list"
            >
                {mediaItems.map(mediaItem => {
                    const itemClassNames = classNames('product-media-slider__item', {
                        'product-media-slider__item--is-video': mediaItem.video,
                    });

                    return (
                        <li key={mediaItem.id} className={itemClassNames}>
                            <div className="product-media-slider__media-wrapper">
                                {!mediaItem.video && mediaItem.image && (
                                    <Picture
                                        {...mediaItem.image}
                                        className="product-media-slider__picture"
                                        imageClassName="product-media-slider__image"
                                    />
                                )}

                                {mediaItem.video && (
                                    <VideoTheatre
                                        {...mediaItem.video}
                                        poster={mediaItem.video.poster || mediaItem.image}
                                        className="product-media-slider__video"
                                    />
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
